import * as Yup from 'yup';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { FACEBOOK_LINK_REGEX, LINKEDIN_LINK_REGEX, TWITTER_LINK_REGEX } from '@/helpers/common';
import TextareaField from '@/components/UI/TextareaField';
import { InputTextField } from '@/components/UI/InputTextField';
import CountrySelectField from '@/components/UI/CountrySelectField';
import { RoundButton } from '@/components/UI/RoundButton';
import './index.scss';

const VALIDATION_SCHEMA = t =>
  Yup.object({
    first_name: Yup.string()
      .max(40, t('profile_about_form.first_name_exceeds_limit'))
      .required(t('profile_about_form.first_name_required')),
    last_name: Yup.string()
      .max(40, t('profile_about_form.last_name_exceeds_limit'))
      .required(t('profile_about_form.last_name_required')),
    citizenship: Yup.string().required(t('profile_about_form.citizenship_required')),
    description: Yup.string().max(1000, t('profile_about_form.about_me_exceeds_limit')),
    facebook: Yup.string().matches(FACEBOOK_LINK_REGEX, t('profile_about_form.incorrect_link')),
    twitter: Yup.string().matches(TWITTER_LINK_REGEX, t('profile_about_form.incorrect_link')),
    linkedin: Yup.string().matches(LINKEDIN_LINK_REGEX, t('profile_about_form.incorrect_link')),
  });

const ProfileAboutForm = ({ userLocation, user, description, onSubmit, onCancel }) => {
  const { t } = useTranslation('components');

  const getInitialValues = (user, about) => ({
    first_name: (user && user.first_name) || '',
    last_name: (user && user.last_name) || '',
    citizenship: (user && user.citizenship) || '',
    citizenship_code: (user && user.citizenship_code) || '',
    facebook: (user && user.facebook) || '',
    twitter: (user && user.twitter) || '',
    linkedin: (user && user.linkedin) || '',
    description: about || '',
  });

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(user, description, userLocation)}
      validationSchema={VALIDATION_SCHEMA(t)}
      onSubmit={(values, formikBag) => onSubmit(values, formikBag)}
    >
      {({ values, setFieldValue, errors, touched, handleChange, handleSubmit }) => (
        <form className="profile-personal-form" onSubmit={handleSubmit}>
          <h1 className="profile-personal-form__title f-32 f-500">
            {t('profile_about_form.edit_personal_information')}
          </h1>
          <div className="profile-personal-form__grid">
            <div className="profile-personal-form__grid-left">
              <InputTextField
                label={t('profile_about_form.first_name_label')}
                name="first_name"
                value={values.first_name}
                onChange={handleChange}
                onClear={() => setFieldValue('first_name', '')}
                error={errors.first_name && touched.first_name && errors.first_name}
                className="profile-personal-form__field"
              />
              <InputTextField
                label={t('profile_about_form.last_name_label')}
                name="last_name"
                value={values.last_name}
                onChange={handleChange}
                onClear={() => setFieldValue('last_name', '')}
                error={errors.last_name && touched.last_name && errors.last_name}
                className="profile-personal-form__field"
              />
            </div>
            <div className="profile-personal-form__grid-right">
              <CountrySelectField
                label={t('profile_about_form.citizenship_label')}
                name="citizenship"
                value={values.citizenship_code}
                onChange={(country, code) => {
                  setFieldValue('citizenship', country);
                  setFieldValue('citizenship_code', code);
                }}
                error={errors.citizenship && touched.citizenship && errors.citizenship}
                className="profile-personal-form__field"
              />
            </div>
          </div>

          <h2 className="profile-personal-form__subtitle f-20 f-500">
            {t('profile_about_form.about_me')}
          </h2>
          <TextareaField
            label=""
            name="description"
            minRows={4}
            value={values.description}
            placeholder={t('profile_about_form.introduce_yourself')}
            onChange={handleChange}
            limit={1000}
            error={errors.description && touched.description && errors.description}
          />

          <h2 className="profile-personal-form__subtitle f-20 f-500">
            {t('profile_about_form.social_networks')}
          </h2>
          <div className="profile-personal-form__grid">
            <InputTextField
              label={t('profile_about_form.facebook_link')}
              name="facebook"
              value={values.facebook}
              onChange={handleChange}
              onClear={() => setFieldValue('facebook', '')}
              error={errors.facebook && touched.facebook && errors.facebook}
              className="profile-personal-form__field"
            />
            <InputTextField
              label={t('profile_about_form.linkedin_link')}
              name="linkedin"
              value={values.linkedin}
              onChange={handleChange}
              onClear={() => setFieldValue('linkedin', '')}
              error={errors.linkedin && touched.linkedin && errors.linkedin}
              className="profile-personal-form__field"
            />
            <InputTextField
              label={t('profile_about_form.twitter_link')}
              name="twitter"
              value={values.twitter}
              onChange={handleChange}
              onClear={() => setFieldValue('twitter', '')}
              error={errors.twitter && touched.twitter && errors.twitter}
              className="profile-personal-form__field"
            />
          </div>

          <div className="profile-personal-form__buttons">
            {onCancel && (
              <RoundButton
                label={t('profile_about_form.cancel')}
                type="button"
                fullWidth
                onClick={onCancel}
                className="profile-personal-form__button"
              />
            )}
            <RoundButton
              label={t('profile_about_form.save')}
              type="submit"
              onSubmit={handleSubmit}
              fillBackground
              fullWidth
              className="profile-personal-form__button"
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default ProfileAboutForm;
